import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { isEmpty } from "lodash-es";

import type { IQuery } from "@citrine/client";
import {
  RefreshClient,
  getQueryCacheFromConfig,
  normalizeQuery,
} from "@citrine/client";

import { useConfiguration } from "../configuration";
import type { IDataObjectOrTemplate } from "../data-object";

import type {
  GemdDataQuery,
  JsonApiResponse,
  MaterialAndMeasurements,
  MaterialAndMeasurementsPageResult,
  MaterialTag,
  MaterialsAndMeasurementsMetadata,
  MaterialsQueryParams,
  SampleCountResponse,
  SortEntry,
  TerminalMaterialAttribute,
} from "./data-query";
import { MATERIAL_ATTRIBUTE_QUERY } from "./data-query";

type AttributeType = "property" | "condition";

/** JSON:API sort string: comma-separated fields, leading "-" for descending. */
export function serializeSort(entries: SortEntry[]): string {
  return entries
    .map(({ field, order }) => `${order === "desc" ? "-" : ""}${field}`)
    .join(",");
}

interface MaterialAttributeQuery extends IQuery {
  dataset_ids?: string[];
  is_material_consumed?: boolean;
  is_material_produced?: boolean;
  attribute_type?: AttributeType;
}

export class TeamDataQueryClient extends RefreshClient {
  readonly endpoint: string;
  readonly endpointV2: string;

  protected constructor(public readonly teamId: string) {
    super();
    this.endpoint = `/api/v1/teams/${teamId}/gemd/query`;
    this.endpointV2 = `/api/v2/teams/${teamId}/gemd/query`;
  }

  static getInstance(teamId: string) {
    return new this(teamId);
  }

  async find(query: GemdDataQuery) {
    const result = await this.request<
      {
        item: IDataObjectOrTemplate;
        metadata?: MaterialsAndMeasurementsMetadata;
      }[]
    >(this.findStorablesConfig(query));
    return result.map((data) => data.item);
  }

  useFind(
    query: GemdDataQuery,
    options?: Omit<
      UseQueryOptions<IDataObjectOrTemplate[]>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery({
      ...options,
      enabled: this.isEnabled() && (options?.enabled ?? true),
      queryKey: getQueryCacheFromConfig(this.findStorablesConfig(query)),
      queryFn: () => this.find(query),
    });
  }

  async findWithMeasurementsPage(
    query: GemdDataQuery,
    params: MaterialsQueryParams,
  ): Promise<MaterialAndMeasurementsPageResult> {
    const response = await this.request<
      JsonApiResponse<MaterialAndMeasurements>
    >(this.findWithMeasurementsPageConfig(query, params));
    return {
      materials: response.data.map((resource) => resource.attributes),
      meta: { page: response.meta.page },
    };
  }

  useFindWithMeasurementsPage(
    query: GemdDataQuery,
    params: MaterialsQueryParams,
    options?: Omit<
      UseQueryOptions<MaterialAndMeasurementsPageResult, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryResult<MaterialAndMeasurementsPageResult, AxiosError> {
    const config = this.findWithMeasurementsPageConfig(query, params);
    return useQuery({
      ...options,
      enabled: this.isEnabled() && (options?.enabled ?? true),
      queryKey: getQueryCacheFromConfig(config),
      queryFn: () => this.findWithMeasurementsPage(query, params),
    });
  }

  /**
   * Fetch every page of materials with measurements and concatenate them into a
   * single array.
   *
   * The first page is requested to discover the total number of pages, then the
   * remaining pages are requested in parallel and stitched together in order.
   *
   * @param query parameters to filter the request
   * @param params page size and sort for each underlying page request
   * @returns all materials with measurements across every page
   */
  async findAllWithMeasurements(
    query: GemdDataQuery,
    params: Omit<MaterialsQueryParams, "pageNumber">,
  ): Promise<MaterialAndMeasurements[]> {
    const firstPage = await this.findWithMeasurementsPage(query, {
      ...params,
      pageNumber: 1,
    });

    const { total_pages } = firstPage.meta.page;

    const remainingPages = await Promise.all(
      Array.from({ length: total_pages - 1 }, (_, index) =>
        this.findWithMeasurementsPage(query, {
          ...params,
          pageNumber: index + 2,
        }),
      ),
    );

    return [firstPage, ...remainingPages].flatMap((page) => page.materials);
  }

  useFindAllWithMeasurements(
    query: GemdDataQuery,
    params: Partial<Omit<MaterialsQueryParams, "pageNumber">> = {},
    options?: Omit<
      UseQueryOptions<MaterialAndMeasurements[], AxiosError>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryResult<MaterialAndMeasurements[], AxiosError> {
    const maxMaterialsPaginationPageSize = useConfiguration(
      "maxMaterialsPaginationPageSize",
    );
    const config = this.findWithMeasurementsPageConfig(query, {
      pageNumber: 0,
      pageSize: params.pageSize ?? maxMaterialsPaginationPageSize,
    });
    return useQuery({
      ...options,
      queryKey: [...getQueryCacheFromConfig(config), "all"],
      queryFn: () =>
        this.findAllWithMeasurements(query, {
          pageSize: maxMaterialsPaginationPageSize,
          ...params,
        }),
    });
  }

  getSampleCount(query: GemdDataQuery) {
    return this.request<SampleCountResponse>({
      method: "POST",
      url: `${this.endpoint}/material-histories-summary`,
      data: query,
    });
  }

  useGetSampleCount(
    query: GemdDataQuery,
    options?: Omit<
      UseQueryOptions<SampleCountResponse, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery<SampleCountResponse, AxiosError>({
      ...options,
      queryKey: [
        `${this.endpoint}/material-histories-summary`,
        query.datasets,
        query.criteria,
      ],
      queryFn: () => this.getSampleCount(query),
      // avoid polluting logs in unit tests when teamId is undefined
      enabled: this.isEnabled() && (options?.enabled ?? true),
    });
  }

  useGetMaterialAttributes(
    query?: MaterialAttributeQuery,
    options?: Omit<
      UseQueryOptions<TerminalMaterialAttribute[], Error>,
      "queryKey" | "queryFn"
    >,
  ) {
    const dataset_ids = query?.dataset_ids
      ? [...query.dataset_ids].sort()
      : undefined;
    const config = {
      method: "GET",
      url: `${this.endpoint}/material-attributes`,
      params: normalizeQuery({ ...query, dataset_ids }),
    };

    return useQuery({
      ...options,
      enabled: this.isEnabled() && (options?.enabled ?? true),
      queryKey: getQueryCacheFromConfig(config),
      queryFn: () => this.request<TerminalMaterialAttribute[]>(config),
    });
  }

  async getMaterialAttributesForMultipleTypes({
    datasets,
    materialTypes,
    attribute_type,
  }: {
    datasets: string[];
    materialTypes: (keyof typeof MATERIAL_ATTRIBUTE_QUERY)[];
    attribute_type?: AttributeType;
  }) {
    const configs = materialTypes.map((type) => {
      switch (type) {
        case "isolated":
          return this.materialAttributesConfig({
            dataset_ids: datasets,
            attribute_type,
            ...MATERIAL_ATTRIBUTE_QUERY.isolated,
          });
        case "raw_and_isolated":
          return this.materialAttributesConfig({
            dataset_ids: datasets,
            attribute_type,
            ...MATERIAL_ATTRIBUTE_QUERY.raw_and_isolated,
          });
        default:
          return this.materialAttributesConfig({
            dataset_ids: datasets,
            attribute_type,
            ...MATERIAL_ATTRIBUTE_QUERY.terminal,
          });
      }
    });

    const responses = await Promise.all(
      configs.map((config) =>
        this.request<TerminalMaterialAttribute[]>(config),
      ),
    );
    return responses.flat();
  }

  useGetMaterialAttributesForMultipleTypes<
    Selection = TerminalMaterialAttribute[],
  >(
    props: {
      datasets: string[];
      materialTypes: (keyof typeof MATERIAL_ATTRIBUTE_QUERY)[];
      attribute_type?: AttributeType;
    },
    options?: Omit<
      UseQueryOptions<TerminalMaterialAttribute[], Error, Selection>,
      "queryKey" | "queryFn"
    >,
  ) {
    const dataset_ids = props.datasets.toSorted();
    return useQuery({
      ...options,
      enabled: this.isEnabled() && (options?.enabled ?? true),
      queryKey: [
        `${this.endpoint}/material-attributes`,
        normalizeQuery({ ...props, dataset_ids }),
      ],
      queryFn: () => this.getMaterialAttributesForMultipleTypes(props),
    });
  }

  getConditionsForProperty({
    propertyTemplateId,
    datasetIds,
  }: {
    propertyTemplateId: string;
    datasetIds?: string[];
  }) {
    return this.request<TerminalMaterialAttribute[]>({
      method: "GET",
      url: `${this.endpointV2}/conditions-for-property`,
      params: normalizeQuery({
        property_template_id: propertyTemplateId,
        dataset_ids: datasetIds,
      }),
    });
  }

  useGetConditionsForProperty<Selection = TerminalMaterialAttribute[]>(
    props: {
      propertyTemplateId: string | undefined;
      datasetIds?: string[];
    },
    options?: Omit<
      UseQueryOptions<TerminalMaterialAttribute[], Error, Selection>,
      "queryKey" | "queryFn" | "enabled"
    >,
  ) {
    const { propertyTemplateId, datasetIds } = props;
    const sortedDatasetIds = datasetIds?.toSorted();
    return useQuery({
      ...options,
      enabled: this.isEnabled() && Boolean(propertyTemplateId),
      queryKey: [
        `${this.endpointV2}/conditions-for-property`,
        normalizeQuery({
          property_template_id: propertyTemplateId,
          dataset_ids: sortedDatasetIds,
        }),
      ],
      queryFn: () =>
        propertyTemplateId === undefined
          ? Promise.resolve<TerminalMaterialAttribute[]>([])
          : this.getConditionsForProperty({
              propertyTemplateId,
              datasetIds: sortedDatasetIds,
            }),
    });
  }

  getMaterialTags(datasetIds: string[]) {
    return this.request<MaterialTag[]>({
      method: "GET",
      url: `${this.endpoint}/material-tags`,
      params: { dataset_ids: datasetIds },
    });
  }

  getMaterialTagsCacheKey() {
    return [`${this.endpoint}/material-tags`];
  }

  useGetMaterialTags(
    datasetIds: string[],
    options?: Omit<UseQueryOptions<MaterialTag[]>, "queryKey" | "queryFn">,
  ): UseQueryResult<MaterialTag[]> {
    const sortedDatasetIds = datasetIds.toSorted();
    return useQuery({
      ...options,
      enabled: this.isEnabled() && (options?.enabled ?? true),
      queryKey: [
        ...this.getMaterialTagsCacheKey(),
        JSON.stringify(sortedDatasetIds),
      ],
      queryFn: () => this.getMaterialTags(sortedDatasetIds),
    });
  }

  protected findStorablesConfig(query: GemdDataQuery) {
    return {
      method: "POST",
      url: `${this.endpoint}/storables`,
      data: normalizeQuery(query),
      params: { per_page: 0 },
    };
  }

  protected findWithMeasurementsPageConfig(
    query: GemdDataQuery,
    params: MaterialsQueryParams,
  ) {
    const sortEntries = params.sort ?? [];

    return {
      url: `/api/v2/teams/${this.teamId}/gemd/query/materials-and-measurements`,
      method: "POST",
      data: normalizeQuery({
        ...query,
        ...(query?.datasets ? { datasets: query.datasets.toSorted() } : {}),
      }),
      params: normalizeQuery({
        "page[number]": params.pageNumber,
        "page[size]": params.pageSize,
        ...(isEmpty(sortEntries) ? {} : { sort: serializeSort(sortEntries) }),
      }),
    };
  }

  protected isEnabled() {
    return Boolean(this.teamId);
  }

  protected materialAttributesConfig(query?: MaterialAttributeQuery) {
    const dataset_ids = query?.dataset_ids
      ? [...query.dataset_ids].sort()
      : undefined;
    return {
      method: "GET",
      url: `${this.endpoint}/material-attributes`,
      params: normalizeQuery({ ...query, dataset_ids }),
    };
  }
}
